// frontend/src/hooks/useScrollToHash.js
import { useEffect } from "react";

// #PrivacyPolicy has its own dedicated handler (PrivacyPolicyModal) — it's
// not a section to scroll to, so this hook leaves it alone.
const SKIP_HASHES = ["#PrivacyPolicy"];

/**
 * Generic support for dynamically-named on-page anchors. Any element whose
 * `id` matches the current URL hash is scrolled into view — e.g. a Tower
 * or Apartment CTA button whose label the admin panel set to "Book Site
 * Visit" gets id="booksitevisit" (see utils/slug.js), so a link straight
 * to yourproject.com/#booksitevisit scrolls right to it.
 *
 * The target section is very often not in the DOM yet the moment the hash
 * is first read — it may still be loading from the API (useProject) or
 * sitting in a lazy-loaded chunk (GallerySection, TowerSection, Footer,
 * etc.) that hasn't finished downloading, especially on a slow connection.
 * A MutationObserver watches the page for the element to appear rather
 * than assuming it's already there, and gives up after 15s if it never
 * shows (bad/stale hash).
 */
export function useScrollToHash() {
  useEffect(() => {
    let observer;
    let timeoutId;

    const tryScroll = () => {
      const hash = window.location.hash;
      if (!hash || SKIP_HASHES.includes(hash)) return true; // nothing to do

      const id = decodeURIComponent(hash.slice(1));
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      }
      return false;
    };

    const watch = () => {
      observer?.disconnect();
      clearTimeout(timeoutId);

      if (tryScroll()) return;

      observer = new MutationObserver(() => {
        if (tryScroll()) {
          observer.disconnect();
          clearTimeout(timeoutId);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });

      timeoutId = setTimeout(() => observer?.disconnect(), 15000);
    };

    watch();
    window.addEventListener("hashchange", watch);

    return () => {
      window.removeEventListener("hashchange", watch);
      observer?.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);
}
